import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getBookDetails } from '../services/bookService';
import ItemDetail from './shared/ItemDetail';
import './BookDetail.css';

function BookDetail() {
  const { id } = useParams();
  const location = useLocation();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  // Extract the book ID directly from the URL path if not available as a parameter
  const extractedId = useMemo(() => {
    if (id) return id;
    
    // If id is not available, try to extract it from the path
    const pathMatch = location.pathname.match(/\/book\/(.+)/);
    if (pathMatch && pathMatch[1]) {
      return pathMatch[1];
    }
    return null;
  }, [id, location.pathname]);

  // Get data passed via state if available - wrapped in useMemo to avoid creating
  // a new empty object on every render when location.state is null
  const stateData = useMemo(() => {
    // First try to get data from location.state (React Router)
    if (location.state) {
      return location.state;
    }
    
    // If no location.state, try to find data in localStorage for this book
    const bookId = extractedId;
    if (bookId) {
      try {
        // Try direct lookup with clean ID (remove works/ if present)
        const cleanId = bookId.replace('works/', '');
        const directKey = `book_${cleanId}_data`;
        
        let localData = localStorage.getItem(directKey);
        if (localData) {
          return JSON.parse(localData);
        }
        
        // If direct lookup fails, scan all localStorage keys
        const keys = Object.keys(localStorage);
        
        // Find any book key that contains our ID
        const matchingKey = keys.find(key => 
          key.startsWith('book_') && 
          key.includes(cleanId)
        );
        
        if (matchingKey) {
          localData = localStorage.getItem(matchingKey);
          if (localData) {
            return JSON.parse(localData);
          }
        }
      } catch (error) {
        console.error('Failed to retrieve data from localStorage:', error);
      }
    }
    
    // Return empty object if no data is available
    return {};
  }, [location.state, extractedId]);

  useEffect(() => {
    const fetchBookDetails = async () => {
      try {
        setLoading(true);
        setRetrying(false);
        
        // Use the extracted ID for API calls
        let bookId = extractedId;
        
        if (!bookId) {
          throw new Error('Book ID not found in URL');
        }
        
        // Fetch book data from API
        const data = await getBookDetails(bookId);
        
        // Merge data from API with data from state to ensure we have the most complete information
        const mergedData = {
          ...data,
          // Ensure first_publish_date is properly set from state if not in API data
          first_publish_date: data.first_publish_date || stateData.first_publish_date || undefined,
          // Ensure authors are properly set from state if not in API data
          authors: data.authors && data.authors.length > 0 ? data.authors : 
                  (stateData.authors && stateData.authors.length > 0 ? stateData.authors : undefined),
          // Add year from state data if available
          year: data.year || stateData.first_publish_date || undefined
        };
        
        setBook(mergedData);
        setError(null);
      } catch (err) {
        console.error('Error fetching book details:', err);
        setError('Failed to fetch book details. Please try again later.');
        setRetrying(true);
        
        // If we had state data, still use that even if API call failed
        if (Object.keys(stateData).length > 0) {
          // Create a minimal book object with state data, ensuring first_publish_date is included
          const fallbackBook = {
            title: stateData.title,
            first_publish_date: stateData.first_publish_date,
            year: stateData.first_publish_date, 
            authors: stateData.authors
          };
          setBook(fallbackBook);
          setError(null);
          setRetrying(false);
        } else {
          // Try again after a delay if it's a network error
          if (err.message && (err.message.includes('network') || err.message.includes('fetch'))) {
            setTimeout(() => {
              setRetrying(false);
              fetchBookDetails();
            }, 3000);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBookDetails();
  }, [extractedId, stateData]);

  // Format the description to handle HTML content safely
  const formatDescription = (description) => {
    if (typeof description === 'object' && description.value) {
      return description.value;
    }
    return description || 'No description available';
  };

  // Create image component for the book
  const bookImageComponent = book?.coverUrl ? (
    <img
      src={book.coverUrl}
      alt={book.title}
      className="item-image book-cover"
    />
  ) : null;

  // Create metadata object for the book, including publication date from state if needed
  const bookMetadata = book ? {
    'Author(s)': book.authors ? book.authors.map(a => a.name).join(', ') : 'Unknown',
    // Multi-stage fallback for the First Published date
    'First Published': (() => {
      // Try to get the year directly from state data first
      if (stateData && stateData.first_publish_date) {
        return stateData.first_publish_date;
      }
      
      // Next try to get it from the book object
      if (book.first_publish_date) {
        return book.first_publish_date;
      }
      
      if (book.year) {
        return book.year;
      }
      
      // Last resort: try to extract from localStorage directly using the book title
      if (book.title) {
        try {
          const keys = Object.keys(localStorage);
          for (const key of keys) {
            if (key.startsWith('book_')) {
              const data = JSON.parse(localStorage.getItem(key));
              if (data.title === book.title && data.first_publish_date) {
                return data.first_publish_date;
              }
            }
          }
        } catch (error) {
          console.error('Error searching localStorage by title:', error);
        }
      }
      
      return 'Unknown';
    })(),
    'Subjects': book.subjects ? <span className="subject-value">{book.subjects.slice(0, 5).join(', ')}</span> : 'Unknown',
    'Rating': book.ratings_average ? `${book.ratings_average.toFixed(1)}/5` : 'Not rated'
  } : {};

  // Create extra content for the book
  const bookExtraContent = book?.description || (book?.links && book.links.length > 0) ? (
    <>
      {book.description && (
        <div className="item-description book-description">
          <h3>Description</h3>
          <p dangerouslySetInnerHTML={{ __html: formatDescription(book.description) }}></p>
        </div>
      )}
      {book.links && book.links.length > 0 && (
        <div className="item-links book-links">
          <h3>External Links</h3>
          <ul>
            {book.links.map((link, index) => (
              <li key={index}>
                <a href={link.url} target="_blank" rel="noopener noreferrer">
                  {link.title || 'Learn more'}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  ) : null;

  return (
    <ItemDetail
      type="book"
      item={book}
      loading={loading}
      error={error}
      retrying={retrying}
      imageComponent={bookImageComponent}
      metadata={bookMetadata}
      extraContent={bookExtraContent}
    />
  );
}

export default BookDetail; 